import Vue from 'vue';
import Vuex from 'vuex';
import i18n from '@/i18n';
import axiosPkg from 'axios';
import { theme } from '@/../tailwind.config.js';
import advancedSearch from './modules/advancedSearch';

Vue.use(Vuex);

const API_URL = process.env.VUE_APP_API_URL;
const axios = axiosPkg.create({
  headers: { Authorization: 'bearer ' + process.env.VUE_APP_API_BEARER }
});

export default new Vuex.Store({
  state: {
    language: {
      locale: i18n.locale,
      flagCode: null,
      restricted: null
    },
    loader: {
      isLoading: false,
      loader: 'dots',
      fullPage: false,
      background: 'transparent',
      opacity: 1,
      color: theme.extend.colors.darkblue
    },
    apiData: {
      vehicles: [],
      makers: [],
      models: [],
      types: [],
      selected: {
        vehicle: null,
        make: null,
        model: null,
        type: null
      },
      kba: {
        types: [],
        selected: null,
        notFound: false
      },
      vin: {
        types: [],
        selected: null,
        notFound: false
      },
      licensePlate: {
        types: [],
        selected: null,
        notFound: false
      },
      results: [],
      clientVehicle: {}
    }
  },
  mutations: {
    SET_LANGUAGE (state, { flagCode, locale, restricted = false }) {
      state.language.locale = locale;
      state.language.flagCode = flagCode;
      state.language.restricted = restricted;
      i18n.locale = locale;
    },
    SET_SELECTED (state, { key, value }) {
      state.apiData.selected[key] = value;
    },
    SET_VEHICLES (state, payload) {
      state.apiData.vehicles = payload;
    },
    SET_MAKERS (state, payload) {
      state.apiData.makers = payload;
    },
    SET_MODELS (state, payload) {
      state.apiData.models = payload;
    },
    SET_TYPES (state, payload) {
      state.apiData.types = payload;
    },
    SET_TYPES_BY_KBA (state, payload) {
      state.apiData.kba.types = payload;
    },
    SET_TYPE_BY_KBA (state, payload) {
      state.apiData.kba.selected = payload;
    },
    SET_TYPES_BY_VIN (state, payload) {
      state.apiData.vin.types = payload;
    },
    SET_TYPES_BY_LICENSEPLATE (state, payload) {
      state.apiData.licensePlate.types = payload;
    },
    SET_RESULTS (state, payload) {
      state.apiData.results = payload;
    },
    SET_LOADING (state, payload) {
      state.loader.isLoading = payload;
    },
    SET_KBA_ERROR (state, payload) {
      state.apiData.kba.notFound = payload;
    },
    SET_VIN_ERROR (state, payload) {
      state.apiData.vin.notFound = payload;
    },
    SET_LICENSEPLATE_ERROR (state, payload) {
      state.apiData.licensePlate.notFound = payload;
    },
    SET_CLIENT_VEHICLE (state, payload) {
      state.apiData.clientVehicle = payload;
    },
    UNSET_SELECTED (state, { selected, data }) {
      for (const key of data) {
        state.apiData[key] = [];
      }
      for (const key of selected) {
        state.apiData.selected[key] = null;
      }
    }
  },
  actions: {
    async GET_VEHICLES ({ commit, state }) {
      try {
        const { data: { response } } = await axios.get(`${API_URL}/${state.language.locale}/getVehicles`);
        commit('SET_VEHICLES', response);
      } catch (err) {
        console.log(err);
        this._vm.$toasted.show(err);
      }
    },
    async GET_MAKERS ({ commit, state }) {
      try {
        const { data: { response } } = await axios.get(`${API_URL}/${state.language.locale}/getMakers/${state.apiData.selected.vehicle}`);
        commit('SET_MAKERS', response);
      } catch (err) {
        console.log(err);
        this._vm.$toasted.show(err);
      }
    },
    async GET_MODELS ({ commit, state }) {
      try {
        const { data: { response } } = await axios.get(`${API_URL}/${state.language.locale}/getModels/${state.apiData.selected.make}`);
        commit('SET_MODELS', response);
      } catch (err) {
        console.log(err);
        this._vm.$toasted.show(err);
      }
    },
    async GET_TYPES ({ commit, state }) {
      try {
        const { data: { response } } = await axios.get(`${API_URL}/${state.language.locale}/getTypes/${state.apiData.selected.model}`);
        commit('SET_TYPES', response);
      } catch (err) {
        console.log(err);
        this._vm.$toasted.show(err);
      }
    },
    async GET_TYPES_BY_KBA ({ commit, dispatch, state }, { hsn, tsn }) {
      commit('SET_KBA_ERROR', false);
      try {
        const countryOfRegistration = state.advancedSearch.selectedCountry;
        const { data } = await axios.get(`${API_URL}/${state.language.locale}/getLicensePlate2TypeID/${hsn + tsn}/${countryOfRegistration.toLowerCase()}`);
        if (data.error) return commit('SET_KBA_ERROR', true);
        if (data.response.length === 1) {
          dispatch('GET_RESULTS', data.response[0].value);
          commit('SET_TYPES_BY_KBA', data.response[0].value);
        } else {
          commit('SET_TYPES_BY_KBA', data.response);
          return 'multiple found.';
        }
      } catch (err) {
        console.log(err);
        this._vm.$toasted.show(err);
      }
    },
    async GET_TYPES_BY_VIN ({ commit, dispatch, state }, { vin }) {
      commit('SET_VIN_ERROR', false);
      const countryOfRegistration = state.advancedSearch.selectedCountry;
      try {
        const { data } = await axios.get(`${API_URL}/${state.language.locale}/getVIN2TypeID/${vin}${countryOfRegistration ? `/${countryOfRegistration.toLowerCase()}` : ''}`);
        if (data.error) return commit('SET_VIN_ERROR', true);
        if (data.response.length === 1) {
          dispatch('GET_RESULTS', data.response[0].value);
          commit('SET_TYPES_BY_VIN', data.response[0].value);
        } else {
          commit('SET_TYPES_BY_VIN', data.response);
          return 'multiple found.';
        }
      } catch (err) {
        console.log(err);
        this._vm.$toasted.show(err);
      }
    },
    async GET_TYPES_BY_LICENSEPLATE ({ commit, dispatch, state }, { licensePlate }) {
      const countryOfRegistration = state.advancedSearch.selectedCountry;
      commit('SET_LICENSEPLATE_ERROR', false);
      try {
        const { data } = await axios.get(`${API_URL}/${state.language.locale}/getLicensePlate2TypeID/${licensePlate.replaceAll(' ', '')}${countryOfRegistration ? `/${countryOfRegistration.toLowerCase()}` : ''}`);
        if (data.error) return commit('SET_LICENSEPLATE_ERROR', true);
        if (data.response.length === 1) {
          dispatch('GET_RESULTS', data.response[0].value);
          commit('SET_TYPES_BY_LICENSEPLATE', data.response[0].value);
        } else {
          commit('SET_TYPES_BY_LICENSEPLATE', data.response);
          return 'multiple found.';
        }
      } catch (err) {
        console.log(err);
        this._vm.$toasted.show(err);
      }
    },
    async GET_RESULTS ({ commit, dispatch, state }, typeId) {
      try {
        dispatch('SET_LOADING', true);
        const { data: { response } } = await axios.get(`${API_URL}/${state.language.locale}/getTypeID2Recommendation/${typeId}`);
        // console.log(response);
        commit('SET_RESULTS', response.brandrangee[0].components);
        commit('SET_CLIENT_VEHICLE', response.vehicle);
        dispatch('SET_LOADING', false);
      } catch (err) {
        console.log(err);
        this._vm.$toasted.show(i18n.t('sidebar.notFound'));
        dispatch('SET_LOADING', false);
      }
    },
    async SET_LOADING ({ commit }, status) {
      commit('SET_LOADING', status);
    },
    async UNSET_SELECTED ({ commit }, payload) {
      commit('UNSET_SELECTED', payload);
    }
  },
  modules: {
    advancedSearch
  }
});
